// Shared entity-path helpers used across the app.
// Intentionally lightweight (no external deps) so it can be reused anywhere.

// Import for local use AND re-export for consumers.
import { normalizeGuid, guidEquals, guidKey } from "./guid";
export { normalizeGuid, guidEquals, guidKey };

export type GUID = string;

export interface EntityLike {
  entity_guid: GUID;
  name: string;
  entity_type?: string;
  is_active?: boolean;
}

export interface EntityRelationshipLike {
  parent_entity_guid: GUID;
  child_entity_guid: GUID;
  is_active?: boolean;
}

function buildEntityMap(entities: EntityLike[]): Record<string, EntityLike> {
  const map: Record<string, EntityLike> = {};
  for (const e of entities ?? []) {
    const k = normalizeGuid(e?.entity_guid);
    if (k) map[k] = e;
  }
  return map;
}

function buildParentMap(rels: EntityRelationshipLike[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const r of rels ?? []) {
    const child = normalizeGuid(r?.child_entity_guid);
    const parent = normalizeGuid(r?.parent_entity_guid);
    if (child && parent) map[child] = parent;
  }
  return map;
}

export function pathFromLeafGuid(
  leafGuid: string | null | undefined,
  entities: EntityLike[],
  relationships: EntityRelationshipLike[],
  maxDepth = 20
): EntityLike[] {
  const leaf = normalizeGuid(leafGuid);
  if (!leaf) return [];

  const entityMap = buildEntityMap(entities ?? []);
  const parentMap = buildParentMap(relationships ?? []);

  const out: EntityLike[] = [];
  const seen = new Set<string>();

  let cur = leaf;
  let depth = 0;
  while (cur && depth < maxDepth) {
    if (seen.has(cur)) break; // safety against cycles
    seen.add(cur);

    const ent = entityMap[cur];
    if (ent) out.push(ent);

    cur = parentMap[cur] ?? "";
    depth++;
  }

  // out is leaf->root; reverse to root->leaf
  return out.reverse();
}

export function geoPartsFromLeafGuid(
  leafGuid: string | null | undefined,
  entities: EntityLike[],
  relationships: EntityRelationshipLike[]
): { l1: string; l2: string; l3: string; parts: string[] } {
  const path = pathFromLeafGuid(leafGuid, entities, relationships);
  const parts = path.map((p) => (p?.name ?? "").trim()).filter(Boolean);

  // Conventional geo hierarchy in this app: Country -> State/Region -> City/Locality
  const l1 = parts[0] ?? "";
  const l2 = parts[1] ?? "";
  const l3 = parts[2] ?? "";

  return { l1, l2, l3, parts };
}

export function pathDisplay(parts: string[]): string {
  return (parts ?? []).filter(Boolean).join(", ");
}
